"use client";

import { useMemo } from "react";

import useSWR from "swr";

import { CasinoSectionType } from "@/features/v2/home/components/CasinoSection/enums";
import { CasinoGame } from "@/features/v2/home/components/CasinoSection/interface";
import { getCasinoData } from "@/features/v2/shared/services/casinoDataService";
import { useAuthStore } from "@/stores/userStore";

type CasinoSWRKey = readonly ["casino-data", CasinoSectionType, string, string];

interface UseCasinoDataReturn {
  games: CasinoGame[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<CasinoGame[] | undefined>;
}

const sortFavoriteIdsKey = (ids: string[]): string => {
  return [...ids].sort().join(",");
};

const fetchCasinoData = async ([, type, instanceId, favoriteIdsKey]: CasinoSWRKey): Promise<CasinoGame[]> => {
  if (type !== CasinoSectionType.FAVORITES) {
    return getCasinoData(type, instanceId || undefined);
  }

  const favoriteIds = favoriteIdsKey === "" ? [] : favoriteIdsKey.split(",");
  return getCasinoData(type, instanceId || undefined, favoriteIds);
};

export function useCasinoData(type: CasinoSectionType, instanceId?: string, initialGames?: CasinoGame[]): UseCasinoDataReturn {
  const favoriteGamesRaw = useAuthStore((state) => {
    return state.account?.favoriteGames;
  });

  const favoriteIdsKey = useMemo(() => {
    if (type !== CasinoSectionType.FAVORITES) {
      return "";
    }

    return sortFavoriteIdsKey(favoriteGamesRaw ?? []);
  }, [type, favoriteGamesRaw]);

  const swrKey: CasinoSWRKey = ["casino-data", type, instanceId ?? "", favoriteIdsKey];
  const hasServerBatch = initialGames !== undefined;

  const { data, error, isLoading, mutate } = useSWR(swrKey, fetchCasinoData, {
    fallbackData: initialGames,
    keepPreviousData: true,
    revalidateOnFocus: false,
    revalidateOnMount: type === CasinoSectionType.FAVORITES || !hasServerBatch
  });

  return {
    games: data ?? [],
    isLoading: Boolean(isLoading && !data),
    error: error instanceof Error ? error.message : error ? String(error) : null,
    refetch: () => {
      return mutate();
    }
  };
}

export function useCasinoGames(instanceId?: string) {
  return useCasinoData(CasinoSectionType.CASINO, instanceId);
}

export function useZingaGames(instanceId?: string) {
  return useCasinoData(CasinoSectionType.ZINGA, instanceId);
}

export function useFavoritesGames(instanceId?: string) {
  return useCasinoData(CasinoSectionType.FAVORITES, instanceId);
}

export function useGamesDataFactory(type: CasinoSectionType, instanceId?: string) {
  return useCasinoData(type, instanceId);
}
